import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { pushFinFoxMessage } from "@/lib/finfoxBus";
import { logGateEvent } from "@/lib/learningTelemetry";

const KEY = "finplay:finfox-question-day";

/**
 * One question a day, delivered by FinFox on whichever screen the student is
 * already on. Two taps, answer revealed immediately, no destination to visit.
 * Read-only: it never touches portfolio, XP or streak data.
 */
const QUESTIONS: { concept: string; title: string; body: string; options: { label: string; correct?: boolean }[]; explanation: string }[] = [
  {
    concept: "pe-ratio",
    title: "Quick one",
    body: "A company trades at a P/E of 60 while its sector sits near 20. What does that actually mean?",
    options: [
      { label: "It's expensive relative to peers", correct: true },
      { label: "It's a better company" },
      { label: "Its shares cost more rupees" },
    ],
    explanation:
      "P/E is price per rupee of profit. At 60 you pay ₹60 for ₹1 of annual earnings versus ₹20 for peers — the market is pricing in much faster growth. If that growth doesn't arrive, the price falls even if the business is fine.",
  },
  {
    concept: "diversification",
    title: "Quick one",
    body: "You hold five stocks — all private banks. Are you diversified?",
    options: [
      { label: "Yes, five is enough" },
      { label: "No, they move together", correct: true },
      { label: "Only if they're large-cap" },
    ],
    explanation:
      "Diversification is about uncorrelated risk, not the count. Five banks all react to the same rate decision and credit cycle, so they behave like one large bet. Spreading across sectors is what lowers the risk.",
  },
  {
    concept: "inflation",
    title: "Quick one",
    body: "Your savings account pays 4% and inflation is 6%. What happened to your money this year?",
    options: [
      { label: "It grew by 4%" },
      { label: "It lost 2% of buying power", correct: true },
      { label: "Nothing changed" },
    ],
    explanation:
      "Only the real return counts: 4% minus 6% is −2%. The balance rose, but it buys less than it did. This is why money left idle for years quietly shrinks.",
  },
  {
    concept: "emergency-fund",
    title: "Quick one",
    body: "Before investing a single rupee, how much should sit in cash you can reach in a day?",
    options: [
      { label: "Three to six months of expenses", correct: true },
      { label: "Nothing — invest it all" },
      { label: "One month's salary" },
    ],
    explanation:
      "Three to six months of expenses. Without it, one hospital bill forces you to sell investments at the worst possible moment — which turns a temporary dip into a permanent loss.",
  },
  {
    concept: "compounding",
    title: "Quick one",
    body: "₹5,000 a month at 12% for 10 years versus ₹10,000 a month for 5 years — same total invested. Which ends bigger?",
    options: [
      { label: "The 10-year one", correct: true },
      { label: "The 5-year one" },
      { label: "They tie" },
    ],
    explanation:
      "The 10-year one, comfortably. Compounding rewards time more than amount, because early rupees earn returns on returns for far longer. Starting sooner beats starting bigger.",
  },
  {
    concept: "debt",
    title: "Quick one",
    body: "A company's profits are growing but its debt is growing faster. What's the risk?",
    options: [
      { label: "Interest eats the profit if growth slows", correct: true },
      { label: "None, growth is growth" },
      { label: "Its share count falls" },
    ],
    explanation:
      "Debt payments are fixed; profits are not. In a slowdown, interest still has to be paid, so a leveraged company's earnings fall much harder than a debt-free peer's. Always read debt alongside growth.",
  },
  {
    concept: "market-timing",
    title: "Quick one",
    body: "The index fell 5% this week. What do most successful long-term investors do?",
    options: [
      { label: "Keep to their plan", correct: true },
      { label: "Sell everything and wait" },
      { label: "Borrow and buy heavily" },
    ],
    explanation:
      "They stick to the plan. Weekly moves are noise, and investors who sell during drops usually buy back higher. Discipline — not prediction — is what produces long-run returns.",
  },
];

export default function FinFoxDailyQuestion() {
  useEffect(() => {
    const today = new Date().toDateString();
    try {
      if (localStorage.getItem(KEY) === today) return;
    } catch {
      return;
    }

    const q = QUESTIONS[new Date().getDate() % QUESTIONS.length];

    const t = setTimeout(async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      const pushed = pushFinFoxMessage({
        id: `question:${today}`,
        kind: "question",
        mood: "smart",
        title: q.title,
        body: q.body,
        question: { concept: q.concept, options: q.options, explanation: q.explanation },
      });

      if (pushed) {
        try {
          localStorage.setItem(KEY, today);
        } catch {
          /* ignore */
        }
        logGateEvent({ userId: user?.id, gate: "finfox-question", status: "shown", concept: q.concept });
      }
    }, 9000);

    return () => clearTimeout(t);
  }, []);

  return null;
}
